import { useEffect, useRef, useState } from "react";
import { getBestof, type BestofItem } from "../api";

export default function BestOf() {
  const [items, setItems] = useState<BestofItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    getBestof()
      .then(setItems)
      .catch((e) => setError(e.message || "Failed to load"));
  }, []);

  useEffect(() => {
    if (!items.length || !containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target as HTMLVideoElement;
          if (entry.intersectionRatio >= 0.6) {
            video.play().catch(() => {
              video.muted = true;
              setMuted(true);
              video.play().catch(() => {});
            });
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }
      },
      { root: containerRef.current, threshold: [0, 0.6, 1] }
    );
    for (const v of videoRefs.current) {
      if (v) observer.observe(v);
    }
    return () => observer.disconnect();
  }, [items]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    for (const v of videoRefs.current) {
      if (v) v.muted = next;
    }
  };

  const togglePlayPause = (e: React.MouseEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  return (
    <div className="h-screen w-screen bg-black text-ivory flex flex-col overflow-hidden">
      <header className="bg-black flex items-center justify-center py-4 border-b border-charcoal">
        <div
          className="font-sans font-black text-lemonade tracking-tighter leading-none"
          style={{ fontSize: "clamp(3rem, 14vw, 6rem)" }}
        >
          llyll
        </div>
      </header>

      {error && (
        <div className="flex-1 flex items-center justify-center text-ash text-ll-body">
          {error}
        </div>
      )}

      <div
        ref={containerRef}
        className="flex-1 overflow-y-scroll snap-y snap-mandatory"
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((item, i) => (
          <section
            key={item.slug}
            className="h-full w-full snap-start snap-always flex items-center justify-center relative"
          >
            <video
              ref={(el) => {
                videoRefs.current[i] = el;
              }}
              src={item.url}
              className="h-full w-full object-contain bg-black cursor-pointer"
              playsInline
              loop
              muted={muted}
              preload={i < 2 ? "auto" : "metadata"}
              onClick={togglePlayPause}
            />
          </section>
        ))}
      </div>

      {items.length > 0 && (
        <button
          onClick={toggleMute}
          aria-label={muted ? "Activer le son" : "Couper le son"}
          className="absolute bottom-6 right-6 z-10 w-12 h-12 rounded-full bg-lemonade text-black flex items-center justify-center shadow-lg"
        >
          {muted ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3L19 9.41 17.59 8 15 10.59 12.41 8 11 9.41 13.59 12 11 14.59 12.41 16 15 13.41 17.59 16 19 14.59 16.59 12z" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
